import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { AlertCircle, Edit, Plus, Trash } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface EquipmentType {
  id: string;
  name: string;
  base_price: number;
  created_at: string;
}

const equipmentTypeSchema = z.object({
  name: z.string().min(2, "Name must be at least 2 characters"),
  base_price: z.coerce.number().min(0, "Price cannot be negative"),
});

export const TeamEquipmentTypesSettings = () => {
  const [equipmentTypes, setEquipmentTypes] = useState<EquipmentType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState<Record<string, boolean>>({});
  const { toast } = useToast();
  const { isAdmin } = useUserRoles();
  
  const form = useForm<z.infer<typeof equipmentTypeSchema>>({
    resolver: zodResolver(equipmentTypeSchema),
    defaultValues: {
      name: "",
      base_price: 0,
    },
  });
  
  useEffect(() => {
    fetchEquipmentTypes();
  }, []);
  
  const fetchEquipmentTypes = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('equipment_types')
        .select('*')
        .order('name');
        
      if (error) throw error;
      
      setEquipmentTypes(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to load equipment types: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const openAddDialog = () => {
    form.reset({
      name: "",
      base_price: 0,
    });
    setIsEditing(false);
    setCurrentId(null);
    setIsDialogOpen(true);
  };
  
  const openEditDialog = (type: EquipmentType) => {
    form.reset({
      name: type.name,
      base_price: type.base_price,
    });
    setIsEditing(true);
    setCurrentId(type.id);
    setIsDialogOpen(true);
  };
  
  const handleDelete = async (id: string) => {
    try {
      setIsDeleting(prev => ({ ...prev, [id]: true }));
      
      // With the cascade delete in place, we can simply delete the equipment type
      const { error } = await supabase
        .from('equipment_types')
        .delete()
        .eq('id', id);
        
      if (error) {
        console.error("Deletion error details:", error);
        throw error;
      }
      
      setEquipmentTypes(equipmentTypes.filter(type => type.id !== id));
      toast({
        title: "Equipment type deleted",
        description: "The team equipment type has been successfully deleted",
      });
    } catch (error: any) {
      console.error("Error deleting equipment type:", error);
      toast({
        title: "Error",
        description: `Failed to delete equipment type: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(prev => ({ ...prev, [id]: false }));
    }
  };
  
  const onSubmit = async (values: z.infer<typeof equipmentTypeSchema>) => {
    try {
      if (isEditing && currentId) {
        const { error } = await supabase
          .from('equipment_types')
          .update({
            name: values.name,
            base_price: values.base_price,
          })
          .eq('id', currentId);
          
        if (error) throw error;
        
        setEquipmentTypes(equipmentTypes.map(type => 
          type.id === currentId ? { ...type, name: values.name, base_price: values.base_price } : type
        ));
        
        toast({
          title: "Equipment type updated",
          description: "The team equipment type has been successfully updated",
        });
      } else {
        // Get current user's ID
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Not authenticated");
        
        const { data, error } = await supabase
          .from('equipment_types')
          .insert({
            name: values.name,
            base_price: values.base_price,
            admin_id: user.id
          })
          .select()
          .single();
          
        if (error) throw error;
        
        setEquipmentTypes([...equipmentTypes, data]);
        
        toast({
          title: "Equipment type added",
          description: "The team equipment type has been successfully added",
        });
      }
      
      setIsDialogOpen(false);
    } catch (error: any) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? 'update' : 'add'} equipment type: ${error.message}`,
        variant: "destructive",
      });
    }
  };
  
  if (!isAdmin) {
    return <div>Only administrators can manage team equipment types.</div>;
  }
  
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Team Equipment Types</h2>
        <Button onClick={openAddDialog} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          Add Equipment Type
        </Button>
      </div>
      
      {isLoading ? (
        <div>Loading equipment types...</div>
      ) : equipmentTypes.length === 0 ? (
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            No team equipment types found. Add some equipment types to get started.
          </AlertDescription>
        </Alert>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Base Price (€)</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {equipmentTypes.map((type) => (
              <TableRow key={type.id}>
                <TableCell>{type.name}</TableCell>
                <TableCell>{type.base_price.toFixed(2)}</TableCell>
                <TableCell>
                  <div className="flex space-x-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => openEditDialog(type)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleDelete(type.id)}
                      disabled={isDeleting[type.id]}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{isEditing ? 'Edit' : 'Add'} Team Equipment Type</DialogTitle>
            <DialogDescription>
              {isEditing 
                ? 'Update the details of this team equipment type' 
                : 'Enter the details for the new team equipment type'}
            </DialogDescription>
          </DialogHeader>
          
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter equipment type name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="base_price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Base Price (€)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        {...field} 
                        step="0.01" 
                        placeholder="0.00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <DialogFooter>
                <Button type="submit">
                  {isEditing ? 'Update' : 'Add'} Equipment Type
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
